import { Pressable, StyleSheet, Text, View } from 'react-native';
import { ContentRenderProps } from '../ContentRender';
import { getUid } from '../../../store/token';
import { color_EDEEF1, color_main, transparent } from '../../../assets/style/colors';
import { useCallback, useEffect, useRef, useState } from 'react';
import ICON_PDF from '../../../assets/svg/ic_doc_pdf.svg';
import ICON_PPT from '../../../assets/svg/ic_doc_ppt.svg';
import ICON_WORD from '../../../assets/svg/ic_doc_word.svg';
import ICON_XLS from '../../../assets/svg/ic_doc_xls.svg';
import ICON_TXT from '../../../assets/svg/ic_doc_txt.svg';
import ICON_UNKNOW from '../../../assets/svg/ic_doc_unknow.svg';
import ICON_ERROR from '../../../assets/svg/ic_download_err.svg';
import { showToast } from '../../../components/Toast';
import { fixRemoteUrl } from '../../../../imsdk/api';
import * as ImSdk from '../../../../imsdk/api';
import { AnimatedCircularProgress } from 'react-native-circular-progress';
import { uploadFileManager, UploadFileManagerCallback } from '../../../utils/uploadFile';
import { CryptEngine } from '../../../utils/CryptEngine';
import { isIos } from '../../../AppEvent';
import { MessageType } from '../../../constants/types';
import { downloadManager } from '../../../utils/DownloadQueue';
import { Msg } from '../../../../imsdk/db/t_msg';

// PWA兼容：使用localStorage + sessionStorage双重标记
const DOWNLOADED_FLAG_PREFIX = 'file_downloaded_';

export const DocumentRender = (props: ContentRenderProps): React.JSX.Element => {
    const isTalk = props.isTalk ?? false;
    const isSender = props.item.sender === getUid();
    const [fileSize, setFileSize] = useState<string>(props.item.meta?.fileSize);
    const [progress, setProgress] = useState<number>(0);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isDownloadError, setIsDownloadError] = useState<boolean>(false);
    const [isFileDownloaded, setIsFileDownloaded] = useState<boolean>(false);
    const fileUniqueKey = useRef('');
    const fileName = props.item.meta?.fileName || '未知文件';

    // 初始化：PWA兼容的下载状态判断
    useEffect(() => {
        const url = fixRemoteUrl(props.item.content) || props.item.upload_path;
        if (url) {
            fileUniqueKey.current = DOWNLOADED_FLAG_PREFIX + CryptEngine.md5(url);
            // 1. 优先读localStorage（持久化），PWA刷新不丢失
            const isPersistDownloaded = localStorage.getItem(fileUniqueKey.current) === 'true';
            // 2. 辅助读sessionStorage（会话级），PWA离线时兜底
            const isSessionDownloaded = sessionStorage.getItem(fileUniqueKey.current) === 'true';
            setIsFileDownloaded(isPersistDownloaded || isSessionDownloaded);
        }
    }, [props.item]);

    // 上传状态监听（保留原有逻辑）
    useEffect(() => {
        let listener: UploadFileManagerCallback;
        const path = (props.item.upload_path || '').replace('file://', '');
        const uri = isIos ? decodeURIComponent(path) : path;
        const key = `${props.item.sender}-${props.item.cid}-${CryptEngine.md5(uri)}`;
        const isUpload = props.item.upload_statue === '0';

        if (isUpload) {
            setIsUploading(true);
            setIsDownloadError(false);
            listener = {
                onUploadProgress(progress) {
                    setProgress(progress);
                },
                onSuccess() {
                    setProgress(100);
                    setIsUploading(false);
                    setIsDownloadError(false);
                },
                onFail() {
                    setProgress(0);
                    setIsUploading(false);
                    setIsDownloadError(true);
                    const message = props.item as any as Msg;
                    if (props.item.upload_path) {
                        message.upload_path = props.item.upload_path;
                        const uid = ImSdk.getInstance().CurrentUid();
                        ImSdk.getInstance().UpsetMsg(uid, message);
                    }
                },
            };
            uploadFileManager.addListener(key, listener);
        } else {
            let remoteUrl = props.item.content || '';
            if (remoteUrl.length > 0) {
                remoteUrl = fixRemoteUrl(remoteUrl);
                downloadManager.getDownloadStatusByUrl(remoteUrl).then(result => {
                    setIsDownloadError(result.status === 'failed');
                }).catch(() => setIsDownloadError(false));
            }
        }

        return () => {
            if (isUpload && listener) {
                uploadFileManager.removeListener(key);
            }
        };
    }, [props.item]);

    // 核心：下载逻辑（简化且稳定）
    const handleDownload = async () => {
        // 双重防重复点击
        if (isDownloading) return;
        
        const localPath = props.item.upload_path ?? '';
        const remoteUrl = props.item.content;
        const url = fixRemoteUrl(remoteUrl) || localPath;

        if (!url) {
            showToast('文件不可用');
            return;
        }

        setIsDownloading(true);
        try {
            // 增加超时控制
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30秒超时

            const res = await fetch(url, {
                signal: controller.signal,
                method: 'GET',
            });

            clearTimeout(timeoutId);
            if (!res.ok) throw new Error(`下载失败：${res.status}`);

            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);

            // 强制指定文件名（100%和界面一致）
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = fileName;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // PWA兼容：双重标记已下载
            localStorage.setItem(fileUniqueKey.current, 'true');
            sessionStorage.setItem(fileUniqueKey.current, 'true');
            setIsFileDownloaded(true);
            showToast(`✅ ${fileName} 下载完成`);

            // 释放临时URL，避免内存泄漏
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (e) {
            const errorMsg = e instanceof Error ? e.message : '下载失败';
            showToast(`❌ ${errorMsg}`);
            setIsDownloadError(true);
        } finally {
            setIsDownloading(false);
        }
    };

    // 点击处理：移除Alert，改用Toast提示 + 直接下载（确保响应）
    const handleClick = () => {
        // 1. 选择状态：执行原有逻辑
        if (props.talkSelectStatus === true) {
            props.onClick?.();
            return;
        }

        // 2. 下载/上传中：提示并返回
        if (isDownloading || isUploading) {
            showToast(isDownloading ? '文件正在下载中...' : '文件正在上传中...');
            return;
        }

        // 3. 已下载：提示并询问是否重新下载（改用confirm，兼容所有环境）
        if (isFileDownloaded) {
            const isConfirm = window.confirm(`「${fileName}」已下载过，是否重新下载？`);
            if (isConfirm) {
                handleDownload();
            }
            return;
        }

        // 4. 未下载：直接下载（确保点击必响应）
        showToast(`开始下载：${fileName}`);
        handleDownload();
    };

    // 文件大小格式化（保留原有逻辑）
    const getFileSizeText = (size: number): string => {
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
        if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
        return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };

    // 文档图标（恢复原有正确逻辑）
    const documentIcon = useCallback((size: number = 35) => {
        if (props.item.type !== MessageType.DOCUMENT || !props.item.meta?.fileName) {
            return <ICON_UNKNOW width={size} height={size} />;
        }
        const extensionIndex = props.item.meta?.fileName?.lastIndexOf('.') ?? -1;
        const extension = extensionIndex !== -1 ? props.item.meta.fileName.substring(extensionIndex) : '';
        if (extension === '.pdf') return <ICON_PDF width={size} height={size} />;
        if (['.doc', '.docx'].includes(extension)) return <ICON_WORD width={size} height={size} />;
        if (['.xls', '.xlsx'].includes(extension)) return <ICON_XLS width={size} height={size} />;
        if (['.ppt', '.pptx'].includes(extension)) return <ICON_PPT width={size} height={size} />;
        if (extension === '.txt') return <ICON_TXT width={size} height={size} />;
        return <ICON_UNKNOW width={size} height={size} />;
    }, [props.item]);

    return (
        <Pressable
            style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: 12,
                paddingVertical: 10,
                backgroundColor: isTalk ? color_EDEEF1 : transparent,
                borderRadius: isTalk ? 8 : 0,
                maxWidth: "100%",
                flexShrink: 1,
                minWidth: 0,
                overflow: 'hidden',
            }}
            onPress={handleClick}
            onLongPress={props.onLongPress}
            // 增加可点击样式反馈（确保用户感知可点击）
            android_ripple={{ color: 'rgba(0,0,0,0.1)' }}
            hitSlop={{ top: 5, bottom: 5, left: 5, right: 5 }}
        >
            {/* 文件名称/大小 */}
            <View style={{ flex: 1, marginRight: 10, minWidth: 0, flexShrink: 1 }}>
                <Text style={styles.fileName} numberOfLines={2} ellipsizeMode='tail'>
                    {fileName}
                </Text>
                <Text style={styles.fileSize}>{getFileSizeText(Number(fileSize) || 0)}</Text>
            </View>

            {/* 图标 + 状态（下载/已下载/进度/错误） */}
            <View style={styles.iconContainer}>
                {documentIcon(36)}

                {/* 下载中/上传中进度 */}
                {(isDownloading || isUploading) && progress < 100 && (
                    <View style={styles.overlay}>
                        <AnimatedCircularProgress
                            size={20}
                            width={3}
                            rotation={0}
                            fill={progress}
                            backgroundColor="#e0e0e0"
                            duration={1}
                            tintColor={color_main}
                        />
                    </View>
                )}

                {/* 下载错误 */}
                {!(isDownloading || isUploading) && isDownloadError && (
                    <View style={styles.overlay}>
                        <ICON_ERROR width={20} height={20} />
                    </View>
                )}

                {/* 未下载：显示文字标记（无新增图标） */}
                {!isFileDownloaded && !isDownloading && !isUploading && !isDownloadError && (
                    <View style={styles.statusTag}>
                        <Text style={styles.downloadTagText}>下载</Text>
                    </View>
                )}

                {/* 已下载：显示文字标记（无新增图标） */}
                {isFileDownloaded && !isDownloading && !isUploading && !isDownloadError && (
                    <View style={styles.statusTag}>
                        <Text style={styles.downloadedTagText}>已下</Text>
                    </View>
                )}
            </View>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    fileName: {
        color: '#333',
        fontSize: 15,
        fontWeight: '500',
        marginBottom: 2,
    },
    fileSize: {
        color: '#999',
        fontSize: 12,
    },
    iconContainer: {
        width: 40,
        height: 40,
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },
    overlay: {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.8)',
    },
    // 状态标记样式（纯文本，无图标）
    statusTag: {
        position: 'absolute',
        bottom: 1,
        right: 1,
        borderRadius: 3,
        paddingHorizontal: 2,
        paddingVertical: 0,
    },
    // 未下载标记文字
    downloadTagText: {
        color: '#666',
        fontSize: 8,
        backgroundColor: 'rgba(255,255,255,0.9)',
    },
    // 已下载标记文字
    downloadedTagText: {
        color: '#4CAF50',
        fontSize: 8,
        fontWeight: 'bold',
        backgroundColor: 'rgba(255,255,255,0.9)',
    },
});